import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import {
    parseFaceSelectionId, faceSelectionId, parseEdgeSelectionId, edgeSelectionId,
    parseVertexSelectionId, vertexSelectionId, looseEdgeSelectionId, parseLooseEdgeSelectionId,
    type SketchFace, type SketchVec3,
} from '../../core/sketch/types';
import {
    parseVcbValue, geometryPools, type InferenceResult, type GeometryPools,
} from '../../core/sketch/inference';
import { pushPullFace, pushPullNeighbors } from '../../core/sketch/pushPull';
import { offsetLoop } from '../../core/sketch/offset';
import { splitFaceWithSegments } from '../../core/sketch/faceSplit';
import { solidSharedEdge, edgeChainKeys, solidVertexPoints, pointKeyOfVector } from '../../core/sketch/topology';
import {
    transformSolid, transformSolidSubobjects, transformSolidEdges, transformSolidCorners,
    edgeKeysAfterTransform, vertexKeysAfterTransform, copySolid, translationMatrix, rotationMatrix,
} from '../../core/sketch/transform';
import { notify } from '../../utils/notify';
import { useSketchToolEnv, screenWorldTolerance } from './sketchInput';
import { foreignSelectionIds, foreignIdUnderHit } from './sketchForeignScene';
import { transformSceneObjects, copySceneObjects, deleteSceneObjects } from '../../core/scene';
import { useSnapStore } from '../snapping/SnapManager';
import {
    registerSketchTool, setSketchFaceDragController, setSketchEdgeDragController,
    setSketchVertexDragController, setSketchLooseEdgeDragController,
    setSketchLooseVertexDragController, setSketchAnnotationDragController,
    sketchDefaultFaceSelect, sketchDefaultSolidEdgeSelect,
    sketchDefaultSolidVertexSelect, sketchDefaultLooseEdgeSelect,
    type SketchFaceDragController, type SketchEdgeDragController, type SketchVertexDragController,
    type SketchLooseEdgeDragController, type SketchLooseVertexDragController,
    type SketchAnnotationDragController,
    type SketchHit,
} from './sketchToolBus';

/**
 * Sketch modify tools (Push/Pull, Move/Copy, Rotate, Offset, Erase).
 *
 * All operate on the face under the cursor and commit one atomic history
 * action per gesture. Push/Pull resolves the push distance through the same
 * inference/VCB pipeline as drawing (type the distance + Enter while dragging).
 */

interface FaceRef { solidId: string; faceId: string }

interface EdgeRef { solidId: string; edgeKey: string }

interface VertexRef { solidId: string; vertexKey: string }

const faceRefOf = (hit: SketchHit): FaceRef | null =>
    hit.kind === 'face' && hit.solidId && hit.faceId
        ? { solidId: hit.solidId, faceId: hit.faceId }
        : null;

const edgeRefOf = (hit: SketchHit): EdgeRef | null =>
    hit.kind === 'solidEdge' && hit.solidId && hit.edgeKey
        ? { solidId: hit.solidId, edgeKey: hit.edgeKey }
        : null;

const vertexRefOf = (hit: SketchHit): VertexRef | null =>
    hit.kind === 'solidVertex' && hit.solidId && hit.vertexKey
        ? { solidId: hit.solidId, vertexKey: hit.vertexKey }
        : null;

/** Resolve the target solid: explicit face hit, else the current selection. */
function targetSolidOf(hit: SketchHit): { solidId: string; faceId?: string } | null {
    if (hit.solidId && hit.faceId) return { solidId: hit.solidId, faceId: hit.faceId };
    const state = useSketchStore.getState();
    for (const id of state.selectedIds) {
        const parsed = parseFaceSelectionId(id);
        if (parsed && state.solids[parsed.solidId]) return parsed;
        if (state.solids[id]) return { solidId: id };
    }
    return null;
}

/** Every solid in the selection (falls back to the explicit hit's solid). */
function targetSolidsOf(hit: SketchHit): string[] {
    if (hit.solidId) return [hit.solidId];
    const state = useSketchStore.getState();
    const ids = new Set<string>();
    for (const id of state.selectedIds) {
        const parsed = parseFaceSelectionId(id);
        if (parsed && state.solids[parsed.solidId]) ids.add(parsed.solidId);
        else if (state.solids[id]) ids.add(id);
    }
    return Array.from(ids);
}

/**
 * Face-level move targets: a face hit NOT in the selection moves alone; a
 * face hit inside the selection (or a plain pick with face composites in
 * the selection) moves every selected face. Empty when neither the hit nor
 * the selection is face-level — the caller falls back to whole solids.
 */
function targetFacesOf(hit: SketchHit): FaceRef[] {
    const state = useSketchStore.getState();
    const hitFace = faceRefOf(hit);
    const hitComposite = hitFace ? faceSelectionId(hitFace.solidId, hitFace.faceId) : null;
    if (hitFace && hitComposite && !state.selectedIds.includes(hitComposite)) {
        return [hitFace];
    }
    const faces = new Map<string, FaceRef>();
    if (hitFace && hitComposite) faces.set(hitComposite, hitFace);
    for (const id of state.selectedIds) {
        const parsed = parseFaceSelectionId(id);
        if (parsed && state.solids[parsed.solidId]?.faces.some((f) => f.id === parsed.faceId)) {
            faces.set(faceSelectionId(parsed.solidId, parsed.faceId), parsed);
        }
    }
    return Array.from(faces.values());
}

/**
 * Edge-level move/rotate targets (the edge mirror of targetFacesOf): a
 * solid-edge hit NOT in the selection moves its whole CHAIN alone (Sketch's
 * Curve entity); a hit inside the selection — or a plain pick with edge
 * composites in the selection — moves every selected edge, expanded to
 * chains. Stale composites (loops rewritten since the pick) are validated
 * against live topology and dropped.
 */
function targetEdgesOf(hit: SketchHit): EdgeRef[] {
    const state = useSketchStore.getState();
    const hitEdge = edgeRefOf(hit);
    const hitComposite = hitEdge ? edgeSelectionId(hitEdge.solidId, hitEdge.edgeKey) : null;
    const dedupe = (refs: EdgeRef[]): EdgeRef[] =>
        refs.filter((ref, i, list) =>
            list.findIndex((other) => other.solidId === ref.solidId && other.edgeKey === ref.edgeKey) === i);
    if (hitEdge && hitComposite && !state.selectedIds.includes(hitComposite)) {
        const solid = state.solids[hitEdge.solidId];
        const chain = solid ? edgeChainKeys(solid, hitEdge.edgeKey) : [];
        return dedupe(chain.map((edgeKey) => ({ solidId: hitEdge.solidId, edgeKey })));
    }
    const edges = new Map<string, EdgeRef>();
    if (hitEdge && hitComposite) {
        const solid = state.solids[hitEdge.solidId];
        const chain = solid ? edgeChainKeys(solid, hitEdge.edgeKey) : [hitEdge.edgeKey];
        chain.forEach((edgeKey) => {
            edges.set(edgeSelectionId(hitEdge.solidId, edgeKey), { solidId: hitEdge.solidId, edgeKey });
        });
    }
    for (const id of state.selectedIds) {
        const parsed = parseEdgeSelectionId(id);
        const solid = parsed ? state.solids[parsed.solidId] : undefined;
        if (parsed && solid && solidSharedEdge(solid, parsed.edgeKey)) {
            edgeChainKeys(solid, parsed.edgeKey).forEach((edgeKey) => {
                edges.set(edgeSelectionId(parsed.solidId, edgeKey), { solidId: parsed.solidId, edgeKey });
            });
        }
    }
    return Array.from(edges.values());
}

/** Group edge refs by owning solid (one transform per solid per gesture). */
function edgesBySolid(edges: EdgeRef[]): Map<string, string[]> {
    const bySolid = new Map<string, string[]>();
    edges.forEach(({ solidId, edgeKey }) => {
        const list = bySolid.get(solidId) ?? [];
        if (!list.includes(edgeKey)) list.push(edgeKey);
        bySolid.set(solidId, list);
    });
    return bySolid;
}

/**
 * Vertex-level move/rotate targets (the vertex mirror of targetEdgesOf): a
 * solid-vertex hit NOT in the selection moves that corner alone; a hit
 * inside the selection — or a plain pick with vertex composites in the
 * selection — moves every selected corner. Stale keys (loops rewritten
 * since the pick) are validated against live contour points and dropped.
 */
function targetVerticesOf(hit: SketchHit): VertexRef[] {
    const state = useSketchStore.getState();
    const hitVertex = vertexRefOf(hit);
    const hitComposite = hitVertex ? vertexSelectionId(hitVertex.solidId, hitVertex.vertexKey) : null;
    if (hitVertex && hitComposite && !state.selectedIds.includes(hitComposite)) {
        return [hitVertex];
    }
    const vertices = new Map<string, VertexRef>();
    if (hitVertex && hitComposite) vertices.set(hitComposite, hitVertex);
    for (const id of state.selectedIds) {
        const parsed = parseVertexSelectionId(id);
        const solid = parsed ? state.solids[parsed.solidId] : undefined;
        if (parsed && solid && solidVertexPoints(solid, parsed.vertexKey).length > 0) {
            vertices.set(vertexSelectionId(parsed.solidId, parsed.vertexKey), {
                solidId: parsed.solidId,
                vertexKey: parsed.vertexKey,
            });
        }
    }
    return Array.from(vertices.values());
}

/** Group vertex refs by owning solid (one transform per solid per gesture). */
function verticesBySolid(vertices: VertexRef[]): Map<string, string[]> {
    const bySolid = new Map<string, string[]>();
    vertices.forEach(({ solidId, vertexKey }) => {
        const list = bySolid.get(solidId) ?? [];
        if (!list.includes(vertexKey)) list.push(vertexKey);
        bySolid.set(solidId, list);
    });
    return bySolid;
}

// ============================ LOOSE EDGE HELPERS ============================

/** One loose-edge endpoint stretch instruction (store action contract). */
export interface LooseStretchItem { id: string; ends: Array<'a' | 'b'> }

const looseEdgeRefOf = (hit: SketchHit): string | null =>
    hit.kind === 'edge' && hit.edgeId ? hit.edgeId : null;

const looseVertexRefOf = (hit: SketchHit): { edgeId: string; end: 'a' | 'b' } | null =>
    hit.kind === 'looseVertex' && hit.edgeId && hit.end
        ? { edgeId: hit.edgeId, end: hit.end }
        : null;

/** Weld key of one loose edge endpoint (same key space as topology). */
const looseKeyOf = (p: SketchVec3): string => pointKeyOfVector(new THREE.Vector3(...p));

/** Loose edge ids behind the current selection (live-validated). */
function selectedLooseEdgeIds(): string[] {
    const state = useSketchStore.getState();
    const ids: string[] = [];
    for (const id of state.selectedIds) {
        const parsed = parseLooseEdgeSelectionId(id);
        if (parsed && state.edges[parsed]) ids.push(parsed);
    }
    return ids;
}

/**
 * Loose-edge move/rotate/scale targets (the loose mirror of targetEdgesOf):
 * a loose-edge/endpoint hit NOT in the selection targets that edge alone; a
 * hit inside the selection — or a plain pick with `looseEdge:` composites in
 * the selection — targets every selected loose edge. Stale ids are
 * validated against live store records and dropped.
 */
function targetLooseEdgeIdsOf(hit: SketchHit): string[] {
    const state = useSketchStore.getState();
    const hitEdge = looseEdgeRefOf(hit) ?? looseVertexRefOf(hit)?.edgeId ?? null;
    const hitComposite = hitEdge ? looseEdgeSelectionId(hitEdge) : null;
    if (hitEdge && hitComposite && !state.selectedIds.includes(hitComposite)) {
        return state.edges[hitEdge] ? [hitEdge] : [];
    }
    const ids = new Set<string>();
    if (hitEdge && state.edges[hitEdge]) ids.add(hitEdge);
    selectedLooseEdgeIds().forEach((id) => ids.add(id));
    return Array.from(ids);
}

/** Weld keys of the given loose edges' endpoints (the moved vertex set). */
function looseEndpointKeys(edgeIds: string[]): Set<string> {
    const edges = useSketchStore.getState().edges;
    const keys = new Set<string>();
    for (const id of edgeIds) {
        const edge = edges[id];
        if (!edge) continue;
        keys.add(looseKeyOf(edge.a));
        keys.add(looseKeyOf(edge.b));
    }
    return keys;
}

/**
 * Stretch items for every loose edge with an endpoint welded (topology-eps
 * keyed) to one of `movedKeys` — the weld-follower expansion that makes a
 * moved segment carry its connected segments, the same vertex-weld read the
 * junction move uses (Sketch: unselected geometry sharing a moved endpoint
 * stretches along).
 */
function looseStretchItems(movedKeys: Set<string>): LooseStretchItem[] {
    const edges = useSketchStore.getState().edges;
    const items: LooseStretchItem[] = [];
    for (const edge of Object.values(edges)) {
        const ends: Array<'a' | 'b'> = [];
        if (movedKeys.has(looseKeyOf(edge.a))) ends.push('a');
        if (movedKeys.has(looseKeyOf(edge.b))) ends.push('b');
        if (ends.length > 0) items.push({ id: edge.id, ends });
    }
    return items;
}

// ============================== PUSH / PULL ==============================

/**
 * Signed drag distance along a face normal from the pointer ray: the
 * closest-point parameter of the normal axis (origin, normal) to the ray —
 * the standard drag-along-axis model gizmos use. Measuring the cursor's
 * perpendicular distance to the face plane can never work here: the input
 * pipeline projects every hit ONTO the active plane, so that distance is
 * pinned to zero and the tool dead-ends with a silent "Zero distance".
 * `fallback` holds the previous distance for rays parallel to the axis
 * (top-down drags have no measurable normal component).
 */
function dragDistanceAlongNormal(
    camera: THREE.Camera,
    domElement: HTMLCanvasElement,
    hit: SketchHit,
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    fallback: number
): number {
    const n = normal.clone().normalize();
    if (!hit.pointerPx) return hit.point.clone().sub(origin).dot(n);
    const rect = domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
        ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
        -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
    );
    camera.updateMatrixWorld();
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    const w0 = raycaster.ray.origin.clone().sub(origin);
    const b = raycaster.ray.direction.dot(n);
    const d = raycaster.ray.direction.dot(w0);
    const e = n.dot(w0);
    const denom = 1 - b * b;
    if (denom < 1e-6) return fallback;
    return (e - b * d) / denom;
}

// ------------------------- push/pull alignment snap -------------------------

/** Quantized point key — same 4-decimal keying as geometryPools' dedupe. */
const snapKey = (x: number, y: number, z: number): string =>
    `${x.toFixed(4)}|${y.toFixed(4)}|${z.toFixed(4)}`;

/**
 * Snap pools without the pushed face's own contour (outer loop, holes,
 * their shared edge midpoints, and a curve face's center): those points sit
 * on the start plane, so leaving them in would pin every push flush at
 * zero. The rest of the solid stays — aligning an extrusion with the same
 * solid's other levels is the classic push/pull snap.
 */
function pushSnapPools(pools: GeometryPools, face: SketchFace): GeometryPools {
    const self = new Set<string>();
    const loops = [face.outer, ...face.holes];
    for (const loop of loops) {
        for (let i = 0; i < loop.length; i++) {
            const a = loop[i];
            const b = loop[(i + 1) % loop.length];
            self.add(snapKey(a[0], a[1], a[2]));
            self.add(snapKey((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2));
        }
    }
    if (face.loopMeta) self.add(snapKey(...face.loopMeta.center));
    return {
        vertices: pools.vertices.filter((v) => !self.has(snapKey(v.x, v.y, v.z))),
        edgeMidpoints: pools.edgeMidpoints.filter((m) => !self.has(snapKey(m.x, m.y, m.z))),
        edges: pools.edges,
        centers: pools.centers.filter((c) => !self.has(snapKey(c.x, c.y, c.z))),
    };
}

/** One aligned snap for a push/pull drag: the resolved distance, the snap
 *  family (inference marker feedback) and the target point itself. */
export interface PushSnapHit {
    distance: number;
    kind: 'endpoint' | 'midpoint' | 'center';
    point: THREE.Vector3;
}

/**
 * Push/pull alignment snapping (Sketch's inference for extrusions): the
 * drag has one degree of freedom — the signed distance along the face
 * normal — so a pool point participates when its HEIGHT along that normal
 * matches the raw drag distance within a screen-derived world tolerance,
 * and the drag snaps to that height. The drawing tools' screen-aperture
 * ladder is the wrong test here: a ray grazing a laterally distant point
 * resolves to an unrelated axis distance, so alignment is measured on the
 * drag axis itself. Governed by the shared OSNAP store (master switch +
 * endpoint/midpoint/center toggles); on-edge/nearest never participates —
 * arbitrary edge heights would stutter the drag.
 */
function snappedPushDistance(
    pools: GeometryPools,
    toleranceAt: (at: THREE.Vector3, px?: number) => number,
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    raw: number
): PushSnapHit | null {
    const snap = useSnapStore.getState();
    if (!snap.enabled) return null;
    const n = normal.clone().normalize();
    // Plain loops over the active families: the best-scan assigns `best`
    // directly, so TypeScript's narrowing stays intact (a closure-assigned
    // accumulator collapses to `never` at the read site).
    const families: Array<{ points: THREE.Vector3[]; kind: PushSnapHit['kind'] }> = [];
    if (snap.types.endpoint) families.push({ points: pools.vertices, kind: 'endpoint' });
    if (snap.types.midpoint) families.push({ points: pools.edgeMidpoints, kind: 'midpoint' });
    if (snap.types.center) families.push({ points: pools.centers, kind: 'center' });
    let best: (PushSnapHit & { gap: number }) | null = null;
    for (const { points, kind } of families) {
        for (const p of points) {
            const height = p.clone().sub(origin).dot(n);
            const gap = Math.abs(height - raw);
            if (gap > toleranceAt(p, snap.aperture)) continue;
            if (!best || gap < best.gap) best = { distance: height, kind, point: p.clone(), gap };
        }
    }
    if (!best) return null;
    return { distance: best.distance, kind: best.kind, point: best.point };
}

export function useSketchPushPullTool() {
    const env = useSketchToolEnv();
    const [active, setActive] = useState<{
        face: FaceRef; origin: THREE.Vector3; normal: THREE.Vector3; pools: GeometryPools;
    } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);
    const [previewDistance, setPreviewDistance] = useState(0);
    const [snapMarker, setSnapMarker] = useState<PushSnapHit | null>(null);
    // Live preview distance for the effect closures (setState is async and
    // the bus registration does not re-run on every preview update).
    const previewRef = useRef(0);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, active ? [active.origin] : [], null);
                setCursor(res);
                if (active) {
                    const raw = dragDistanceAlongNormal(env.camera, env.domElement, hit, active.origin, active.normal, previewRef.current);
                    const snap = snappedPushDistance(active.pools, env.worldToleranceAt, active.origin, active.normal, raw);
                    const distance = snap ? snap.distance : raw;
                    setSnapMarker(snap);
                    previewRef.current = distance;
                    setPreviewDistance(distance);
                }
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, active ? [active.origin] : [], null);
                if (!active) {
                    const faceRef = faceRefOf(hit);
                    if (!faceRef) return;
                    const solid = useSketchStore.getState().solids[faceRef.solidId];
                    const face = solid?.faces.find((f) => f.id === faceRef.faceId);
                    if (!face) return;
                    setActive({
                        face: faceRef,
                        origin: res.point.clone(),
                        normal: new THREE.Vector3(...face.normal).normalize(),
                        pools: pushSnapPools(env.pools, face),
                    });
                    return;
                }
                // Commit: aligned/snapped drag distance (VCB overrides), then push.
                let distance = dragDistanceAlongNormal(env.camera, env.domElement, hit, active.origin, active.normal, previewRef.current);
                const snap = snappedPushDistance(active.pools, env.worldToleranceAt, active.origin, active.normal, distance);
                if (snap) distance = snap.distance;
                const vcb = env.consumeVcb();
                if (vcb !== null) distance = vcb;
                const store = useSketchStore.getState();
                const solid = store.solids[active.face.solidId];
                if (solid) {
                    // SketchUp shared-context cut: the prism also reshapes
                    // every visible, unlocked solid it passes through.
                    const neighbors = pushPullNeighbors(solid, Object.values(store.solids));
                    const result = pushPullFace(solid, active.face.faceId, distance, { neighbors });
                    if (result.ok && result.solid) {
                        const updates = [{ id: solid.id, next: result.solid }];
                        for (const next of result.neighborUpdates ?? []) {
                            updates.push({ id: next.id, next });
                        }
                        store.updateSolids(updates, `Push/Pull ${distance.toFixed(2)}`);
                    } else if (!result.ok && Math.abs(distance) > 1e-9) {
                        // A zero-distance plain click is a no-op gesture;
                        // a real drag that gets rejected must be reported.
                        notify(`Push/Pull rejected: ${result.reason ?? 'unknown error'}`, 'warning');
                    }
                }
                setActive(null);
                previewRef.current = 0;
                setPreviewDistance(0);
                setSnapMarker(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, active]);

    // Escape cancels an in-progress push.
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setActive(null);
            previewRef.current = 0;
            setPreviewDistance(0);
            setSnapMarker(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { active, cursor, previewDistance, snapMarker };
}

/**
 * Select-mode Shift+drag push/pull: pressing a face with Shift arms the
 * gesture (the bus routes it only when no tool owns the pointer), the drag
 * previews the extrusion along the face normal, and release commits — a VCB
 * distance typed during the drag wins. The drag distance resolves through
 * the same alignment snapping as the Push/Pull tool (snappedPushDistance),
 * so model vertices/midpoints/centers align the extrusion while dragging.
 * Releasing without a drag re-applies the standard face select, so
 * shift+click keeps its meaning.
 */
export function useSketchShiftDragPushPull() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<FaceRef | null>(null);
    const [previewDistance, setPreviewDistance] = useState(0);
    const [snapMarker, setSnapMarker] = useState<PushSnapHit | null>(null);
    // Screen-sized alignment tolerance for the push snap (same math as the
    // tool env's worldToleranceAt; this hook mounts without a tool env).
    const toleranceAt = useCallback(
        (at: THREE.Vector3, px?: number): number => screenWorldTolerance(camera, gl.domElement, at, px),
        [camera, gl]
    );
    // Imperative gesture state: the bus callbacks must never read stale
    // closures, and the release listener must not depend on React state.
    const gestureRef = useRef<{
        face: FaceRef;
        origin: THREE.Vector3;
        normal: THREE.Vector3;
        beginHit: SketchHit;
        distance: number;
        pools: GeometryPools;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setPreviewDistance(0);
        setSnapMarker(null);
        if (!commit) return;
        const store = useSketchStore.getState();
        let distance = gesture.distance;
        const vcb = parseVcbValue(store.vcbText);
        store.setVcbText('');
        if (vcb !== null) distance = vcb;
        if (Math.abs(distance) < 1e-9) {
            // Zero-drag shift+click: the standard face select still applies.
            sketchDefaultFaceSelect(gesture.beginHit);
            return;
        }
        const solid = store.solids[gesture.face.solidId];
        if (!solid) return;
        // Same shared-context cut as the Push/Pull tool: the prism also
        // reshapes every visible, unlocked solid it passes through.
        const neighbors = pushPullNeighbors(solid, Object.values(store.solids));
        const result = pushPullFace(solid, gesture.face.faceId, distance, { neighbors });
        if (result.ok && result.solid) {
            const updates = [{ id: solid.id, next: result.solid }];
            for (const next of result.neighborUpdates ?? []) {
                updates.push({ id: next.id, next });
            }
            store.updateSolids(updates, `Push/Pull ${distance.toFixed(2)}`);
        } else {
            notify(`Push/Pull rejected: ${result.reason ?? 'unknown error'}`, 'warning');
        }
    }, []);

    useEffect(() => {
        const controller: SketchFaceDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                const faceRef = faceRefOf(hit);
                if (!faceRef) return false;
                const store = useSketchStore.getState();
                const solid = store.solids[faceRef.solidId];
                const face = solid?.faces.find((f) => f.id === faceRef.faceId);
                if (!face || solid.locked) return false;
                gestureRef.current = {
                    face: faceRef,
                    origin: hit.point.clone(),
                    normal: new THREE.Vector3(...face.normal).normalize(),
                    beginHit: hit,
                    distance: 0,
                    // One O(document) pools pass per gesture (not per move):
                    // the document-wide pools minus the pushed contour.
                    pools: pushSnapPools(geometryPools(store.solids, store.edges, store.tags), face),
                };
                setActive(faceRef);
                setPreviewDistance(0);
                setSnapMarker(null);
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture) return;
                const raw = dragDistanceAlongNormal(
                    camera, gl.domElement, hit, gesture.origin, gesture.normal, gesture.distance
                );
                const snap = snappedPushDistance(gesture.pools, toleranceAt, gesture.origin, gesture.normal, raw);
                gesture.distance = snap ? snap.distance : raw;
                setPreviewDistance(gesture.distance);
                setSnapMarker(snap);
            },
            end: endGesture,
        };
        setSketchFaceDragController(controller);
        return () => setSketchFaceDragController(null);
    }, [camera, gl, toleranceAt, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas (and any
    // face mesh) before the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, previewDistance, snapMarker };
}

// ============================== EDGE DRAG MOVE ==============================

/**
 * Select-mode edge drag (SketchUp's primary edge interaction: press an edge
 * and drag — the edge and every attached face follow). Moving the picked
 * chain moves the whole chain; when the pick belongs to the current edge
 * selection, the whole selection moves. The translation is measured on the
 * VIEW plane through the pick point (normal = camera direction), so the
 * edge tracks the pointer 1:1 on screen from any orbit angle. A VCB value
 * typed during the drag rescales the final offset along its own direction.
 * Releasing without a drag falls through to the standard chain select.
 */
export function useSketchEdgeDragMove() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<{
        solidId: string; edgeKeys: string[]; beginPoint: THREE.Vector3;
    } | null>(null);
    const [offset, setOffset] = useState<THREE.Vector3 | null>(null);
    // Imperative gesture state: bus callbacks must never read stale
    // closures, and the window-level release listener must not depend on
    // React state.
    const gestureRef = useRef<{
        solidId: string;
        edgeKeys: string[];
        beginPoint: THREE.Vector3;
        planeNormal: THREE.Vector3;
        offset: THREE.Vector3;
        beginHit: SketchHit;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setOffset(null);
        if (!commit) return;
        const store = useSketchStore.getState();
        let translation = gesture.offset.clone();
        if (translation.lengthSq() < 1e-12) {
            // Zero-drag click: the standard chain select still applies.
            sketchDefaultSolidEdgeSelect(gesture.beginHit);
            return;
        }
        // A VCB value typed during the drag commits an exact distance along
        // the drag direction (same contract as the Shift+drag push/pull).
        const vcb = parseVcbValue(store.vcbText);
        store.setVcbText('');
        if (vcb !== null) translation = translation.normalize().multiplyScalar(vcb);

        const matrix = translationMatrix(translation);
        const bySolid = edgesBySolid(
            gesture.edgeKeys.map((edgeKey) => ({ solidId: gesture.solidId, edgeKey }))
        );
        const nextSelection: string[] = [];
        bySolid.forEach((edgeKeys, solidId) => {
            const solid = store.solids[solidId];
            if (!solid) return;
            const next = transformSolidEdges(solid, edgeKeys, matrix);
            store.updateSolid(solidId, next, 'Move Edge');
            // Edge keys are position-derived: remap the selection onto the
            // transformed topology so the highlight (and the Gumball) stay
            // alive for the next gesture.
            edgeKeysAfterTransform(solid, edgeKeys, matrix).forEach((key) => {
                if (solidSharedEdge(next, key)) nextSelection.push(edgeSelectionId(solidId, key));
            });
        });
        if (nextSelection.length > 0) {
            store.select(nextSelection);
            useAppStore.getState().select([gesture.solidId]);
        }
    }, []);

    useEffect(() => {
        const controller: SketchEdgeDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                if (hit.kind !== 'solidEdge' || !hit.solidId || !hit.edgeKey) return false;
                const solid = useSketchStore.getState().solids[hit.solidId];
                if (!solid || solid.locked) return false;
                const chain = edgeChainKeys(solid, hit.edgeKey);
                if (chain.length === 0) return false;
                // A pick inside the current edge selection moves the whole
                // selection (expanded to chains); an outside pick moves the
                // picked chain alone.
                const compositeSet = new Set(
                    useSketchStore.getState().selectedIds
                );
                const picked = chain.map((key) => edgeSelectionId(hit.solidId!, key));
                const selectedEdges: string[] = [];
                const moveSelection = picked.some((id) => compositeSet.has(id));
                if (moveSelection) {
                    for (const id of compositeSet) {
                        const parsed = parseEdgeSelectionId(id);
                        if (parsed?.solidId === hit.solidId && solidSharedEdge(solid, parsed.edgeKey)) {
                            selectedEdges.push(parsed.edgeKey);
                        }
                    }
                }
                const edgeKeys = (moveSelection && selectedEdges.length > 0 ? selectedEdges : chain)
                    // Chains of a chain selection can overlap — dedupe.
                    .filter((key, i, list) => list.indexOf(key) === i);
                const planeNormal = camera.getWorldDirection(new THREE.Vector3()).normalize();
                gestureRef.current = {
                    solidId: hit.solidId,
                    edgeKeys,
                    beginPoint: hit.point.clone(),
                    planeNormal,
                    offset: new THREE.Vector3(),
                    beginHit: hit,
                };
                setActive({ solidId: hit.solidId, edgeKeys, beginPoint: hit.point.clone() });
                setOffset(new THREE.Vector3());
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture || !hit.pointerPx) return;
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(gesture.planeNormal, gesture.beginPoint);
                const out = new THREE.Vector3();
                if (!raycaster.ray.intersectPlane(plane, out)) return;
                gesture.offset.copy(out.sub(gesture.beginPoint));
                setOffset(gesture.offset.clone());
            },
            end: endGesture,
        };
        setSketchEdgeDragController(controller);
        return () => setSketchEdgeDragController(null);
    }, [camera, gl, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas (and any
    // edge pick) before the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, offset };
}

// ============================ VERTEX DRAG MOVE ============================

/**
 * Select-mode vertex drag (SketchUp corner move: press a welded corner and
 * drag — the corner and every attached face follow through the weld pool).
 * When the pick belongs to the current vertex selection, the whole
 * selection moves. The translation is measured on the VIEW plane through
 * the pick point (normal = camera direction), so the corner tracks the
 * pointer 1:1 from any orbit angle. A VCB value typed during the drag
 * rescales the final offset along its own direction. Releasing without a
 * drag falls through to the standard vertex select.
 */
export function useSketchVertexDragMove() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<{
        solidId: string; vertexKeys: string[]; beginPoint: THREE.Vector3;
    } | null>(null);
    const [offset, setOffset] = useState<THREE.Vector3 | null>(null);
    // Imperative gesture state: bus callbacks must never read stale
    // closures, and the window-level release listener must not depend on
    // React state.
    const gestureRef = useRef<{
        solidId: string;
        vertexKeys: string[];
        beginPoint: THREE.Vector3;
        planeNormal: THREE.Vector3;
        offset: THREE.Vector3;
        beginHit: SketchHit;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setOffset(null);
        if (!commit) return;
        const store = useSketchStore.getState();
        let translation = gesture.offset.clone();
        if (translation.lengthSq() < 1e-12) {
            // Zero-drag click: the standard vertex select still applies.
            sketchDefaultSolidVertexSelect(gesture.beginHit);
            return;
        }
        // A VCB value typed during the drag commits an exact distance along
        // the drag direction (same contract as the edge drag).
        const vcb = parseVcbValue(store.vcbText);
        store.setVcbText('');
        if (vcb !== null) translation = translation.normalize().multiplyScalar(vcb);

        const matrix = translationMatrix(translation);
        const nextSelection: string[] = [];
        verticesBySolid(
            gesture.vertexKeys.map((vertexKey) => ({ solidId: gesture.solidId, vertexKey }))
        ).forEach((vertexKeys, solidId) => {
            const solid = store.solids[solidId];
            if (!solid) return;
            const corners = vertexKeys.flatMap((key) => solidVertexPoints(solid, key));
            if (corners.length === 0) return;
            const next = transformSolidCorners(solid, corners, matrix);
            store.updateSolid(solidId, next, 'Move Vertex');
            // Vertex keys are position-derived: remap the selection onto the
            // transformed contour points so the highlight (and the Gumball)
            // stay alive for the next gesture.
            vertexKeysAfterTransform(solid, vertexKeys, matrix).forEach((key) => {
                if (solidVertexPoints(next, key).length > 0) nextSelection.push(vertexSelectionId(solidId, key));
            });
        });
        if (nextSelection.length > 0) {
            store.select(nextSelection);
            useAppStore.getState().select([gesture.solidId]);
        }
    }, []);

    useEffect(() => {
        const controller: SketchVertexDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                if (hit.kind !== 'solidVertex' || !hit.solidId || !hit.vertexKey) return false;
                const solid = useSketchStore.getState().solids[hit.solidId];
                if (!solid || solid.locked) return false;
                if (solidVertexPoints(solid, hit.vertexKey).length === 0) return false;
                // A pick inside the current vertex selection moves the whole
                // selection; an outside pick moves the picked corner alone.
                const compositeSet = new Set(
                    useSketchStore.getState().selectedIds
                );
                const picked = vertexSelectionId(hit.solidId, hit.vertexKey);
                const moveSelection = compositeSet.has(picked);
                const vertexKeys: string[] = [];
                if (moveSelection) {
                    for (const id of compositeSet) {
                        const parsed = parseVertexSelectionId(id);
                        if (parsed?.solidId === hit.solidId && solidVertexPoints(solid, parsed.vertexKey).length > 0) {
                            vertexKeys.push(parsed.vertexKey);
                        }
                    }
                }
                const keys = (moveSelection && vertexKeys.length > 0 ? vertexKeys : [hit.vertexKey])
                    .filter((key, i, list) => list.indexOf(key) === i);
                const planeNormal = camera.getWorldDirection(new THREE.Vector3()).normalize();
                gestureRef.current = {
                    solidId: hit.solidId,
                    vertexKeys: keys,
                    beginPoint: hit.point.clone(),
                    planeNormal,
                    offset: new THREE.Vector3(),
                    beginHit: hit,
                };
                setActive({ solidId: hit.solidId, vertexKeys: keys, beginPoint: hit.point.clone() });
                setOffset(new THREE.Vector3());
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture || !hit.pointerPx) return;
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(gesture.planeNormal, gesture.beginPoint);
                const out = new THREE.Vector3();
                if (!raycaster.ray.intersectPlane(plane, out)) return;
                gesture.offset.copy(out.sub(gesture.beginPoint));
                setOffset(gesture.offset.clone());
            },
            end: endGesture,
        };
        setSketchVertexDragController(controller);
        return () => setSketchVertexDragController(null);
    }, [camera, gl, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas (and any
    // vertex pick) before the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, offset };
}

// ========================= LOOSE EDGE DRAG MOVE =========================

/**
 * Select-mode loose-edge drag (SketchUp's line move: press a drawn segment
 * and drag). The moved vertex set is the picked edge's two endpoints — or
 * every selected loose edge's endpoints when the pick sits inside the
 * current loose-edge selection — expanded through the endpoint weld pool,
 * so connected segments stretch along exactly like the junction move. The
 * translation is measured on the VIEW plane through the pick point (normal
 * = camera direction), so the line tracks the pointer 1:1 from any orbit
 * angle. A VCB value typed during the drag rescales the final offset along
 * its own direction. Releasing without a drag falls through to the standard
 * loose-edge select.
 */
export function useSketchLooseEdgeDragMove() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<{
        edgeIds: string[]; items: LooseStretchItem[]; beginPoint: THREE.Vector3;
    } | null>(null);
    const [offset, setOffset] = useState<THREE.Vector3 | null>(null);
    // Imperative gesture state: bus callbacks must never read stale
    // closures, and the window-level release listener must not depend on
    // React state.
    const gestureRef = useRef<{
        items: LooseStretchItem[];
        beginPoint: THREE.Vector3;
        planeNormal: THREE.Vector3;
        offset: THREE.Vector3;
        beginHit: SketchHit;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setOffset(null);
        if (!commit) return;
        const store = useSketchStore.getState();
        let translation = gesture.offset.clone();
        if (translation.lengthSq() < 1e-12) {
            // Zero-drag click: the standard loose-edge select still applies.
            sketchDefaultLooseEdgeSelect(gesture.beginHit);
            return;
        }
        // A VCB value typed during the drag commits an exact distance along
        // the drag direction (same contract as the solid edge drag).
        const vcb = parseVcbValue(store.vcbText);
        store.setVcbText('');
        if (vcb !== null) translation = translation.normalize().multiplyScalar(vcb);
        store.stretchLooseEdges(gesture.items, translation, 'Move Edge');
    }, []);

    useEffect(() => {
        const controller: SketchLooseEdgeDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                const edgeId = looseEdgeRefOf(hit);
                if (!edgeId) return false;
                const edgeIds = targetLooseEdgeIdsOf(hit);
                if (edgeIds.length === 0) return false;
                const items = looseStretchItems(looseEndpointKeys(edgeIds));
                if (items.length === 0) return false;
                const planeNormal = camera.getWorldDirection(new THREE.Vector3()).normalize();
                gestureRef.current = {
                    items,
                    beginPoint: hit.point.clone(),
                    planeNormal,
                    offset: new THREE.Vector3(),
                    beginHit: hit,
                };
                setActive({ edgeIds, items, beginPoint: hit.point.clone() });
                setOffset(new THREE.Vector3());
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture || !hit.pointerPx) return;
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(gesture.planeNormal, gesture.beginPoint);
                const out = new THREE.Vector3();
                if (!raycaster.ray.intersectPlane(plane, out)) return;
                gesture.offset.copy(out.sub(gesture.beginPoint));
                setOffset(gesture.offset.clone());
            },
            end: endGesture,
        };
        setSketchLooseEdgeDragController(controller);
        return () => setSketchLooseEdgeDragController(null);
    }, [camera, gl, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas (and any
    // edge pick) before the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, offset };
}

// ========================= LOOSE VERTEX DRAG MOVE =========================

/**
 * Select-mode loose-endpoint drag (SketchUp endpoint stretch: press the
 * start or end of a drawn segment and drag). The moved vertex set is the
 * grabbed endpoint's weld cluster — every coincident loose endpoint moves,
 * the far ends stay and the segments elongate. The translation is measured
 * on the VIEW plane through the grab point; a VCB value typed during the
 * drag rescales the final offset along its own direction. Releasing without
 * a drag falls through to the owning edge's select.
 */
export function useSketchLooseVertexDragMove() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<{
        edgeId: string; end: 'a' | 'b'; items: LooseStretchItem[]; beginPoint: THREE.Vector3;
    } | null>(null);
    const [offset, setOffset] = useState<THREE.Vector3 | null>(null);
    const gestureRef = useRef<{
        items: LooseStretchItem[];
        beginPoint: THREE.Vector3;
        planeNormal: THREE.Vector3;
        offset: THREE.Vector3;
        beginHit: SketchHit;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setOffset(null);
        if (!commit) return;
        const store = useSketchStore.getState();
        let translation = gesture.offset.clone();
        if (translation.lengthSq() < 1e-12) {
            // Zero-drag click: the owning edge's select still applies.
            sketchDefaultLooseEdgeSelect(gesture.beginHit);
            return;
        }
        const vcb = parseVcbValue(store.vcbText);
        store.setVcbText('');
        if (vcb !== null) translation = translation.normalize().multiplyScalar(vcb);
        store.stretchLooseEdges(gesture.items, translation, 'Stretch Edge');
    }, []);

    useEffect(() => {
        const controller: SketchLooseVertexDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                const vertexRef = looseVertexRefOf(hit);
                if (!vertexRef) return false;
                const edge = useSketchStore.getState().edges[vertexRef.edgeId];
                if (!edge) return false;
                const items = looseStretchItems(new Set([looseKeyOf(edge[vertexRef.end])]));
                if (items.length === 0) return false;
                const planeNormal = camera.getWorldDirection(new THREE.Vector3()).normalize();
                gestureRef.current = {
                    items,
                    beginPoint: hit.point.clone(),
                    planeNormal,
                    offset: new THREE.Vector3(),
                    beginHit: hit,
                };
                setActive({ edgeId: vertexRef.edgeId, end: vertexRef.end, items, beginPoint: hit.point.clone() });
                setOffset(new THREE.Vector3());
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture || !hit.pointerPx) return;
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(gesture.planeNormal, gesture.beginPoint);
                const out = new THREE.Vector3();
                if (!raycaster.ray.intersectPlane(plane, out)) return;
                gesture.offset.copy(out.sub(gesture.beginPoint));
                setOffset(gesture.offset.clone());
            },
            end: endGesture,
        };
        setSketchLooseVertexDragController(controller);
        return () => setSketchLooseVertexDragController(null);
    }, [camera, gl, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas (and any
    // endpoint pick) before the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, offset };
}

// ======================== ANNOTATION DRAG MOVE ========================

/**
 * Select-mode annotation drag (SketchUp annotation move: press a dimension
 * or text label and drag it aside). A dimension repositions its dim line
 * (the measured points stay pinned, the witness lines re-foot and the value
 * keeps its meaning); a text label rides whole (anchor + leader). The
 * translation is measured on the VIEW plane through the pick point. A clean
 * click without a drag is swallowed — annotations have no click-select
 * contract yet.
 */
export function useSketchAnnotationDragMove() {
    const { camera, gl } = useThree();
    const [active, setActive] = useState<{
        type: 'dimension' | 'label'; id: string;
    } | null>(null);
    const [offset, setOffset] = useState<THREE.Vector3 | null>(null);
    const gestureRef = useRef<{
        type: 'dimension' | 'label';
        id: string;
        beginPoint: THREE.Vector3;
        planeNormal: THREE.Vector3;
        offset: THREE.Vector3;
    } | null>(null);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        gestureRef.current = null;
        setActive(null);
        setOffset(null);
        if (!commit) return;
        const translation = gesture.offset.clone();
        if (translation.lengthSq() < 1e-12) return;
        const store = useSketchStore.getState();
        if (gesture.type === 'dimension') {
            store.moveDimensions([gesture.id], translation, 'Move Dimension');
        } else {
            store.moveLabels([gesture.id], translation, 'Move Text');
        }
    }, []);

    useEffect(() => {
        const controller: SketchAnnotationDragController = {
            get dragging() { return gestureRef.current !== null; },
            begin: (hit) => {
                if (hit.kind !== 'annotation' || !hit.annotationId) return false;
                const store = useSketchStore.getState();
                const type = store.dimensions[hit.annotationId]
                    ? 'dimension'
                    : store.labels[hit.annotationId] ? 'label' : null;
                if (!type) return false;
                const planeNormal = camera.getWorldDirection(new THREE.Vector3()).normalize();
                gestureRef.current = {
                    type,
                    id: hit.annotationId,
                    beginPoint: hit.point.clone(),
                    planeNormal,
                    offset: new THREE.Vector3(),
                };
                setActive({ type, id: hit.annotationId });
                setOffset(new THREE.Vector3());
                return true;
            },
            drag: (hit) => {
                const gesture = gestureRef.current;
                if (!gesture || !hit.pointerPx) return;
                const rect = gl.domElement.getBoundingClientRect();
                const ndc = new THREE.Vector2(
                    ((hit.pointerPx.x - rect.left) / rect.width) * 2 - 1,
                    -((hit.pointerPx.y - rect.top) / rect.height) * 2 + 1
                );
                camera.updateMatrixWorld();
                const raycaster = new THREE.Raycaster();
                raycaster.setFromCamera(ndc, camera);
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(gesture.planeNormal, gesture.beginPoint);
                const out = new THREE.Vector3();
                if (!raycaster.ray.intersectPlane(plane, out)) return;
                gesture.offset.copy(out.sub(gesture.beginPoint));
                setOffset(gesture.offset.clone());
            },
            end: endGesture,
        };
        setSketchAnnotationDragController(controller);
        return () => setSketchAnnotationDragController(null);
    }, [camera, gl, endGesture]);

    // Release/cancel at window level: the drag may leave the canvas before
    // the gesture ends.
    useEffect(() => {
        const onUp = (e: PointerEvent): void => {
            if (e.button === 0) endGesture(true);
        };
        const onCancel = (): void => endGesture(false);
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') endGesture(false);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('blur', onCancel);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            window.removeEventListener('blur', onCancel);
            window.removeEventListener('keydown', onKey);
        };
    }, [endGesture]);

    return { active, offset };
}

// ============================== MOVE / COPY ==============================

export function useSketchMoveTool(copy = false) {
    const env = useSketchToolEnv();
    // Face/edge/vertex targets move subobjects (transformSolidSubobjects /
    // transformSolidCorners); solid targets move whole solids; loose edges
    // stretch through their endpoint weld pool. A gesture is either one —
    // resolved on the first click, subobject levels win when the pick (or
    // the selection) is subobject-level.
    const [base, setBase] = useState<{
        solidIds: string[]; faces: FaceRef[]; edges: EdgeRef[]; vertices: VertexRef[];
        looseIds: string[]; looseItems: LooseStretchItem[]; origin: THREE.Vector3;
        /** Foreign (CAD/BIM) citizens from the shared selection that ride
         *  the same gesture through the cross-workbench edit router. */
        foreignIds: string[];
    } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, base ? [base.origin] : [], null);
                setCursor(res);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, base ? [base.origin] : [], null);
                if (!base) {
                    // SketchUp semantics: a pick inside the selection (or any
                    // non-subobject pick) moves the WHOLE selection; a pick
                    // on unselected geometry moves just that entity.
                    const hitEdge = edgeRefOf(hit);
                    const hitFace = faceRefOf(hit);
                    const hitVertex = vertexRefOf(hit);
                    const hitLooseEdge = looseEdgeRefOf(hit);
                    const hitLooseVertex = looseVertexRefOf(hit);
                    const selected = useSketchStore.getState().selectedIds;
                    const hitEdgeSelected = !!(hitEdge && selected.includes(
                        edgeSelectionId(hitEdge.solidId, hitEdge.edgeKey)
                    ));
                    const hitFaceSelected = !!(hitFace && hit.faceId && selected.includes(
                        faceSelectionId(hitFace.solidId, hitFace.faceId)
                    ));
                    const hitVertexSelected = !!(hitVertex && selected.includes(
                        vertexSelectionId(hitVertex.solidId, hitVertex.vertexKey)
                    ));
                    const hitLooseId = hitLooseEdge ?? hitLooseVertex?.edgeId ?? null;
                    const hitLooseSelected = !!(hitLooseId && selected.includes(
                        looseEdgeSelectionId(hitLooseId)
                    ));
                    if (hitEdgeSelected || hitFaceSelected || hitVertexSelected || hitLooseSelected
                        || (!hitEdge && !hitFace && !hitVertex && !hitLooseId)) {
                        const edges = targetEdgesOf(hit);
                        const faces = targetFacesOf(hit);
                        const vertices = targetVerticesOf(hit);
                        // Whole-selection moves carry the selected loose edges
                        // too, expanded through their endpoint weld pool.
                        const looseIds = selectedLooseEdgeIds();
                        const looseItems = looseIds.length > 0
                            ? looseStretchItems(looseEndpointKeys(looseIds))
                            : [];
                        // Whole solids from the sketch selection ride the same
                        // gesture when the pick is on one of them (or empty).
                        const wholeSolids = targetSolidsOf(hit);
                        // Mixed selection: foreign citizens (CAD entities, BIM
                        // elements) in the shared selection move with the same
                        // gesture through the edit router — minus the sketch
                        // citizens the native path already owns.
                        const nativeCitizens = new Set<string>([
                            ...looseIds,
                            ...wholeSolids,
                            ...edges.map((e) => e.solidId),
                            ...faces.map((f) => f.solidId),
                            ...vertices.map((v) => v.solidId),
                        ]);
                        const foreignIds = foreignSelectionIds(nativeCitizens);
                        if (edges.length > 0 || faces.length > 0 || vertices.length > 0 || looseItems.length > 0 || wholeSolids.length > 0 || foreignIds.length > 0) {
                            setBase({ solidIds: wholeSolids, faces, edges, vertices, looseIds, looseItems, foreignIds, origin: res.point.clone() });
                            return;
                        }
                        return;
                    }
                    if (hitEdge) {
                        setBase({ solidIds: [], faces: [], edges: targetEdgesOf(hit), vertices: [], looseIds: [], looseItems: [], foreignIds: [], origin: res.point.clone() });
                        return;
                    }
                    if (hitVertex) {
                        setBase({ solidIds: [], faces: [], edges: [], vertices: targetVerticesOf(hit), looseIds: [], looseItems: [], foreignIds: [], origin: res.point.clone() });
                        return;
                    }
                    // A grabbed loose endpoint stretches its weld cluster
                    // (the far ends stay); a grabbed segment rides whole.
                    if (hitLooseVertex) {
                        const edge = useSketchStore.getState().edges[hitLooseVertex!.edgeId];
                        if (!edge) return;
                        setBase({
                            solidIds: [], faces: [], edges: [], vertices: [], looseIds: [],
                            looseItems: looseStretchItems(new Set([looseKeyOf(edge[hitLooseVertex!.end])])),
                            foreignIds: [],
                            origin: res.point.clone(),
                        });
                        return;
                    }
                    if (hitLooseEdge) {
                        setBase({
                            solidIds: [], faces: [], edges: [], vertices: [], looseIds: [hitLooseEdge!],
                            looseItems: looseStretchItems(looseEndpointKeys([hitLooseEdge!])),
                            foreignIds: [],
                            origin: res.point.clone(),
                        });
                        return;
                    }
                    setBase({ solidIds: [], faces: targetFacesOf(hit), edges: [], vertices: [], looseIds: [], looseItems: [], foreignIds: [], origin: res.point.clone() });
                    return;
                }
                let delta = res.point.clone().sub(base.origin);
                const vcb = env.consumeVcb();
                if (vcb !== null && delta.lengthSq() > 1e-12) {
                    delta = delta.normalize().multiplyScalar(vcb);
                }
                const store = useSketchStore.getState();
                // Ctrl (or the dedicated Copy tool) clones instead of moving —
                // Sketch's Move/Copy toggle. A copied subobject cannot live
                // inside the manifold shell, so face/edge/vertex targets
                // just move.
                const doCopy = copy || hit.ctrlKey;
                if (delta.lengthSq() > 1e-12) {
                    const matrix = translationMatrix(delta);
                    if (base.looseItems.length > 0) {
                        // Loose edges stretch through the weld pool; Ctrl
                        // clones the target segments instead (the Move/Copy
                        // toggle — the clones leave the welds behind).
                        if (doCopy && base.looseIds.length > 0) {
                            store.copyLooseEdges(base.looseIds, matrix, 'Copy');
                        } else if (!doCopy) {
                            store.stretchLooseEdges(base.looseItems, delta, 'Move Edge');
                        }
                    } else if (base.edges.length > 0 || base.faces.length > 0 || base.vertices.length > 0) {
                        // Faces, edges and corners of one solid move under
                        // ONE shared weld pool, so their shared corners never
                        // move twice.
                        const bySolid = new Map<string, { faceIds: string[]; edgeKeys: string[]; vertexKeys: string[] }>();
                        base.faces.forEach(({ solidId, faceId }) => {
                            const entry = bySolid.get(solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                            if (!entry.faceIds.includes(faceId)) entry.faceIds.push(faceId);
                            bySolid.set(solidId, entry);
                        });
                        base.edges.forEach(({ solidId, edgeKey }) => {
                            const entry = bySolid.get(solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                            if (!entry.edgeKeys.includes(edgeKey)) entry.edgeKeys.push(edgeKey);
                            bySolid.set(solidId, entry);
                        });
                        base.vertices.forEach(({ solidId, vertexKey }) => {
                            const entry = bySolid.get(solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                            if (!entry.vertexKeys.includes(vertexKey)) entry.vertexKeys.push(vertexKey);
                            bySolid.set(solidId, entry);
                        });
                        const nextSelection: string[] = [];
                        bySolid.forEach(({ faceIds, edgeKeys, vertexKeys }, solidId) => {
                            const solid = store.solids[solidId];
                            if (!solid) return;
                            const next = transformSolidSubobjects(solid, faceIds, edgeKeys, matrix, vertexKeys);
                            store.updateSolid(
                                solidId, next,
                                faceIds.length > 0 ? 'Move Face' : vertexKeys.length > 0 ? 'Move Vertex' : 'Move Edge'
                            );
                            // Edge/vertex keys are position-derived: remap
                            // the selection onto the transformed topology so
                            // the next gesture (gizmo or tool) keeps working.
                            edgeKeysAfterTransform(solid, edgeKeys, matrix).forEach((key) => {
                                if (solidSharedEdge(next, key)) nextSelection.push(edgeSelectionId(solidId, key));
                            });
                            vertexKeysAfterTransform(solid, vertexKeys, matrix).forEach((key) => {
                                if (solidVertexPoints(next, key).length > 0) nextSelection.push(vertexSelectionId(solidId, key));
                            });
                            faceIds.forEach((faceId) => nextSelection.push(faceSelectionId(solidId, faceId)));
                        });
                        if (nextSelection.length > 0) store.select(nextSelection);
                    } else if (doCopy) {
                        base.solidIds.forEach((id) => {
                            const solid = store.solids[id];
                            if (solid) store.addSolid(transformSolid(copySolid(solid), matrix), 'Copy');
                        });
                    } else {
                        base.solidIds.forEach((id) => {
                            const solid = store.solids[id];
                            if (solid) store.updateSolid(id, transformSolid(solid, matrix), 'Move');
                        });
                    }
                    // Foreign citizens (CAD entities, BIM elements, and any
                    // sketch citizens selected through the shared space) ride
                    // the same gesture through the edit router — one routed
                    // step per owner, same world frame (A5).
                    if (base.foreignIds.length > 0) {
                        if (doCopy) copySceneObjects(base.foreignIds, matrix, 'Copy');
                        else transformSceneObjects(base.foreignIds, matrix, 'Move');
                    }
                }
                setBase(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, base, copy]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setBase(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { base, cursor };
}

// ============================== ROTATE ==============================

export function useSketchRotateTool() {
    const env = useSketchToolEnv();
    // Edge/vertex-level selections rotate INSIDE their solids (connected
    // faces follow the weld); loose edge selections rotate as rigid sets;
    // anything else rotates whole solids.
    const [state, setState] = useState<{
        solidIds: string[]; edges: EdgeRef[]; vertices: VertexRef[]; looseIds: string[];
        foreignIds: string[];
        center: THREE.Vector3; ref: THREE.Vector3;
    } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, state ? [state.center] : [], null);
                setCursor(res);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, state ? [state.center] : [], null);
                if (!state) {
                    const edges = targetEdgesOf(hit);
                    const vertices = targetVerticesOf(hit);
                    const looseIds = targetLooseEdgeIdsOf(hit);
                    const foreignIds = foreignSelectionIds(new Set([
                        ...edges.map((e) => e.solidId),
                        ...vertices.map((v) => v.solidId),
                        ...looseIds,
                    ]));
                    if (edges.length > 0 || vertices.length > 0 || looseIds.length > 0 || foreignIds.length > 0) {
                        setState({ solidIds: [], edges, vertices, looseIds, foreignIds, center: res.point.clone(), ref: res.point.clone() });
                        return;
                    }
                    const target = targetSolidOf(hit);
                    if (!target) return;
                    setState({ solidIds: [target.solidId], edges: [], vertices: [], looseIds: [], foreignIds: [], center: res.point.clone(), ref: res.point.clone() });
                    return;
                }
                if (state.center.distanceTo(state.ref) < 1e-6) {
                    // Second click sets the reference direction.
                    setState({ ...state, ref: res.point.clone() });
                    return;
                }
                // Third click commits: Z-axis rotation (Sketch protractor v1).
                const axis = new THREE.Vector3(0, 0, 1);
                const refDir = state.ref.clone().sub(state.center).setZ(0);
                const curDir = res.point.clone().sub(state.center).setZ(0);
                if (refDir.lengthSq() > 1e-12 && curDir.lengthSq() > 1e-12) {
                    let angle = refDir.normalize().angleTo(curDir.normalize());
                    const cross = refDir.x * curDir.y - refDir.y * curDir.x;
                    if (cross < 0) angle = -angle;
                    const vcb = env.consumeVcb();
                    const finalAngle = vcb !== null ? (vcb * Math.PI) / 180 : angle;
                    const store = useSketchStore.getState();
                    const matrix = rotationMatrix(axis, finalAngle, state.center);
                    if (state.edges.length > 0 || state.vertices.length > 0 || state.looseIds.length > 0) {
                        const nextSelection: string[] = [];
                        edgesBySolid(state.edges).forEach((edgeKeys, solidId) => {
                            const solid = store.solids[solidId];
                            if (!solid) return;
                            const next = transformSolidEdges(solid, edgeKeys, matrix);
                            store.updateSolid(solidId, next, 'Rotate Edge');
                            // Re-key the selection onto the rotated topology.
                            edgeKeysAfterTransform(solid, edgeKeys, matrix).forEach((key) => {
                                if (solidSharedEdge(next, key)) nextSelection.push(edgeSelectionId(solidId, key));
                            });
                        });
                        verticesBySolid(state.vertices).forEach((vertexKeys, solidId) => {
                            const solid = store.solids[solidId];
                            if (!solid) return;
                            const corners = vertexKeys.flatMap((key) => solidVertexPoints(solid, key));
                            if (corners.length === 0) return;
                            const next = transformSolidCorners(solid, corners, matrix);
                            store.updateSolid(solidId, next, 'Rotate Vertex');
                            vertexKeysAfterTransform(solid, vertexKeys, matrix).forEach((key) => {
                                if (solidVertexPoints(next, key).length > 0) nextSelection.push(vertexSelectionId(solidId, key));
                            });
                        });
                        // Loose edges have stable ids: a rigid rotate needs
                        // no selection re-keying.
                        if (state.looseIds.length > 0) {
                            store.transformLooseEdges(state.looseIds, matrix, 'Rotate Edge');
                        }
                        if (nextSelection.length > 0) store.select(nextSelection);
                    } else {
                        const solid = store.solids[state.solidIds[0]];
                        if (solid) {
                            store.updateSolid(solid.id, transformSolid(solid, matrix), 'Rotate');
                        }
                    }
                    // Foreign citizens rotate about the same protractor
                    // center through the edit router.
                    if (state.foreignIds.length > 0) {
                        transformSceneObjects(state.foreignIds, matrix, 'Rotate');
                    }
                }
                setState(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, state]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setState(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { state, cursor };
}

// ============================== OFFSET ==============================

export function useSketchOffsetTool() {
    const env = useSketchToolEnv();
    const [active, setActive] = useState<{ face: FaceRef; origin: THREE.Vector3; normal: THREE.Vector3 } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);
    const [previewDistance, setPreviewDistance] = useState(0);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, active ? [active.origin] : [], null);
                setCursor(res);
                if (active) {
                    setPreviewDistance(res.point.distanceTo(active.origin));
                }
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, active ? [active.origin] : [], null);
                if (!active) {
                    const faceRef = faceRefOf(hit);
                    if (!faceRef) return;
                    const solid = useSketchStore.getState().solids[faceRef.solidId];
                    const face = solid?.faces.find((f) => f.id === faceRef.faceId);
                    if (!face) return;
                    setActive({
                        face: faceRef,
                        origin: res.point.clone(),
                        normal: new THREE.Vector3(...face.normal).normalize(),
                    });
                    return;
                }
                let distance = res.point.distanceTo(active.origin);
                const vcb = env.consumeVcb();
                if (vcb !== null) distance = vcb;
                const store = useSketchStore.getState();
                const solid = store.solids[active.face.solidId];
                const face = solid?.faces.find((f) => f.id === active.face.faceId);
                if (solid && face && distance > 1e-6) {
                    // Offset the outer contour and split the face: ring + inner.
                    const inner = offsetLoop(face.outer, active.normal, distance);
                    if (inner.length >= 3) {
                        const closed = [...inner, inner[0]];
                        const result = splitFaceWithSegments(face, [closed]);
                        if (result.ok) {
                            const faces = solid.faces.map((f) => (f.id === face.id ? result.faces : f)).flat();
                            store.updateSolid(solid.id, { ...solid, faces }, `Offset ${distance.toFixed(2)}`);
                        }
                    }
                }
                setActive(null);
                setPreviewDistance(0);
            },
        });
        return () => registerSketchTool(null);
    }, [env, active]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setActive(null);
            setPreviewDistance(0);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { active, cursor, previewDistance };
}

// ============================== ERASE ==============================

export function useSketchEraseTool() {
    // Camera + canvas for the cross-workbench foreign pick (plane hits that
    // land on CAD/BIM geometry).
    const env = useSketchToolEnv();
    useEffect(() => {
        registerSketchTool({
            onDown: (hit) => {
                if (hit.button !== 0) return;
                // Loose edge pick: the segment itself dies (Sketch's eraser).
                if (hit.kind === 'edge' && hit.edgeId) {
                    useSketchStore.getState().removeLooseEdges([hit.edgeId], 'Erase');
                    return;
                }
                // Annotation pick: dimensions/labels erase like geometry.
                if (hit.kind === 'annotation' && hit.annotationId) {
                    const store = useSketchStore.getState();
                    if (store.dimensions[hit.annotationId]) {
                        store.removeDimensions([hit.annotationId], 'Erase');
                    } else if (store.labels[hit.annotationId]) {
                        store.removeLabels([hit.annotationId], 'Erase');
                    }
                    return;
                }
                // Solid edge pick: the border dies WITH the faces it bounds
                // (Sketch's eraser coalesces the two surfaces). A solid left
                // without faces is removed entirely.
                if (hit.kind === 'solidEdge' && hit.solidId && hit.edgeKey) {
                    const store = useSketchStore.getState();
                    const solid = store.solids[hit.solidId];
                    if (!solid || solid.locked) return;
                    const edge = solidSharedEdge(solid, hit.edgeKey);
                    if (!edge) return;
                    if (edge.faceIds.length >= solid.faces.length) {
                        store.removeSolids([solid.id], 'Erase');
                        store.select([]);
                        return;
                    }
                    const drop = new Set(edge.faceIds);
                    const faces = solid.faces.filter((f) => !drop.has(f.id));
                    store.updateSolid(solid.id, { ...solid, faces }, 'Erase Edge');
                    return;
                }
                const target = targetSolidOf(hit);
                if (!target) {
                    // Cross-workbench eraser (B4): a plane hit can still land
                    // on foreign geometry (a CAD entity, a BIM element) —
                    // resolve it under the pointer and route the delete
                    // through the edit router so the owner's undo chain
                    // records it.
                    const foreignId = foreignIdUnderHit(hit, env.camera, env.domElement);
                    if (foreignId) deleteSceneObjects([foreignId], 'Erase');
                    return;
                }
                const store = useSketchStore.getState();
                const solid = store.solids[target.solidId];
                if (!solid) return;
                // Shift+click HIDES instead of deleting (Sketch's eraser modifier).
                if (hit.shiftKey) {
                    store.setSolidsVisible([solid.id], false, 'Hide');
                    return;
                }
                if (!target.faceId || solid.faces.length <= 1) {
                    // Whole solid erase (or its last face).
                    store.removeSolids([solid.id], 'Erase');
                    store.select([]);
                    return;
                }
                // Face erase: drop the face (the shell may become open; that
                // is the Sketch behavior — the user pushes it back later).
                const faces = solid.faces.filter((f) => f.id !== target.faceId);
                store.updateSolid(solid.id, { ...solid, faces }, 'Erase Face');
            },
        });
        return () => registerSketchTool(null);
    }, [env]);
    return {};
}

// ============================== SCALE ==============================

/**
 * Scale: pick solid → base point → reference point → target point. The
 * uniform factor is |base→target| / |base→ref|; a VCB number before the
 * final click commits an exact multiplier. All selected solids scale
 * together about the shared base point.
 */
export function useSketchScaleTool() {
    const env = useSketchToolEnv();
    const [state, setState] = useState<{
        solidIds: string[]; looseIds: string[]; foreignIds: string[];
        base: THREE.Vector3;
        ref: THREE.Vector3 | null;
    } | null>(null);
    const [cursor, setCursor] = useState<InferenceResult | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => {
                const res = env.infer(hit, state ? [state.base] : [], null);
                setCursor(res);
            },
            onDown: (hit) => {
                if (hit.button !== 0) return;
                const res = env.infer(hit, state ? [state.base] : [], null);
                if (!state) {
                    const all = targetSolidsOf(hit);
                    const looseIds = targetLooseEdgeIdsOf(hit);
                    const foreignIds = foreignSelectionIds(new Set([...all, ...looseIds]));
                    if (all.length === 0 && looseIds.length === 0 && foreignIds.length === 0) return;
                    setState({ solidIds: all, looseIds, foreignIds, base: res.point.clone(), ref: null });
                    return;
                }
                if (!state.ref) {
                    if (res.point.distanceTo(state.base) < 1e-9) return;
                    setState({ ...state, ref: res.point.clone() });
                    return;
                }
                // Final click: reference distance → target distance.
                let factor = res.point.distanceTo(state.base) / state.ref.distanceTo(state.base);
                const vcb = env.consumeVcb();
                if (vcb !== null && Math.abs(vcb) > 1e-9) factor = vcb;
                if (Math.abs(factor - 1) > 1e-9 && Number.isFinite(factor)) {
                    const store = useSketchStore.getState();
                    const b = state.base;
                    const matrix = new THREE.Matrix4()
                        .makeTranslation(b.x, b.y, b.z)
                        .multiply(new THREE.Matrix4().makeScale(factor, factor, factor))
                        .multiply(new THREE.Matrix4().makeTranslation(-b.x, -b.y, -b.z));
                    state.solidIds.forEach((id) => {
                        const solid = store.solids[id];
                        if (solid) store.updateSolid(id, transformSolid(solid, matrix), `Scale ${factor.toFixed(2)}x`);
                    });
                    if (state.looseIds.length > 0) {
                        store.transformLooseEdges(state.looseIds, matrix, `Scale ${factor.toFixed(2)}x`);
                    }
                    // Foreign citizens scale about the same base point
                    // through the edit router.
                    if (state.foreignIds.length > 0) {
                        transformSceneObjects(state.foreignIds, matrix, `Scale ${factor.toFixed(2)}x`);
                    }
                }
                setState(null);
            },
        });
        return () => registerSketchTool(null);
    }, [env, state]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            setState(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { state, cursor };
}
